import { FC } from "react";
import { DownOutlined } from "@ant-design/icons";
import { Dropdown } from "../../../design-system";
import { DocumentCard } from "../../../design-system/components/DocumentList/DocumentCard";
import { Project, ProjectAnalysis } from "../../../types/ProjectTypes";
import { getAttachmentDownloadUrl } from "../../../common/api/ProjectClient";
import { ATTACHMENTS_TOUR_ID } from "../tour/reportTourSteps";
import styles from "./AttachmentsDropdown.module.less";

interface AttachmentsDropdownProps {
    project: Project;
    analysis: ProjectAnalysis;
    /** Render the trigger with an empty panel even when there are no attachments,
     * so the report tour has something to point at. */
    forceVisible?: boolean;
}

export const AttachmentsDropdown: FC<AttachmentsDropdownProps> = ({
    project,
    analysis,
    forceVisible = false,
}) => {
    const attachments = analysis.attachments ?? [];
    if (attachments.length === 0 && !forceVisible) {
        return null;
    }

    const uploadedAt =
        analysis.completedDate ?? analysis.submittedDate ?? analysis.recordCreationDate;

    const renderPanel = () => (
        <div className={styles.dropdownPanel}>
            <div className={styles.dropdownTitle}>Attachments</div>
            <div className={styles.divider} />
            <div className={styles.attachmentList}>
                {attachments.length === 0 && (
                    <div className={styles.emptyMessage}>
                        Attachments shared with this report will appear here.
                    </div>
                )}
                {attachments.map((doc) => (
                    <a
                        key={doc.id}
                        href={getAttachmentDownloadUrl(project.id, analysis.version, doc.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.attachmentItem}
                    >
                        <DocumentCard
                            name={doc.name}
                            uploadedBy="InfinityLoop"
                            uploadedAt={uploadedAt}
                        />
                    </a>
                ))}
            </div>
        </div>
    );

    return (
        <Dropdown
            menu={{ items: [] }}
            dropdownRender={renderPanel}
            trigger={["click"]}
            placement="bottomRight"
        >
            <button
                type="button"
                className={styles.attachmentsTrigger}
                data-tour-id={ATTACHMENTS_TOUR_ID}
            >
                Attachments
                <DownOutlined className={styles.chevron} />
            </button>
        </Dropdown>
    );
};
